import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * PWA Hook - Manages PWA installation, push notifications, and service worker
 */
export const usePWA = (appType = 'driver') => {
  // iter 264b — hook now uses the shared axios instance for authed calls so
  // the /notifications/subscribe and /notifications/unsubscribe requests go
  // through the silent-refresh interceptor instead of reading the raw
  // localStorage token.
  const { api } = useAuth();
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscription, setPushSubscription] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [swRegistration, setSwRegistration] = useState(null);

  // Get manifest based on app type
  const getManifestUrl = useCallback(() => {
    switch (appType) {
      case 'driver':
        return '/manifest-driver.json';
      case 'owner-operator':
        return '/manifest-owner-op.json';
      case 'timeclock':
        return '/manifest-timeclock.json';
      case 'dispatch':
      default:
        return '/manifest.json';
    }
  }, [appType]);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('[PWA] Service Worker registered:', registration.scope);
          setSwRegistration(registration);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                console.log('[PWA] New version available');
              }
            });
          });
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });
    }

    // Check push support
    if ('PushManager' in window) {
      setPushSupported(true);
      setPushPermission(Notification.permission);
    }
  }, []);

  // Handle install prompt
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update manifest link
  useEffect(() => {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = getManifestUrl();
    } else {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = getManifestUrl();
      document.head.appendChild(link);
    }
  }, [getManifestUrl]);

  // Install the PWA
  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No install prompt available');
      return false;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[PWA] Install prompt outcome:', outcome);
    
    setDeferredPrompt(null);
    setIsInstallable(false);
    
    return outcome === 'accepted';
  }, [deferredPrompt]);

  // Request push notification permission
  const requestPushPermission = useCallback(async () => {
    if (!pushSupported) {
      console.log('[PWA] Push not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission === 'granted') {
        await subscribeToPush();
        return true;
      }
      return false;
    } catch (error) {
      console.error('[PWA] Push permission error:', error);
      return false;
    }
  }, [pushSupported]);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    try {
      // Wait for service worker to be ready (more reliable than checking swRegistration)
      const reg = swRegistration || await navigator.serviceWorker?.ready;
      if (!reg) {
        console.log('[PWA] No service worker registration available');
        return null;
      }

      // Get VAPID public key from server
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/vapid-public-key`);
      
      let vapidKey;
      if (response.ok) {
        const data = await response.json();
        vapidKey = data.publicKey;
      } else {
        console.log('[PWA] Using default VAPID key');
        vapidKey = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';
      }

      // Check for existing subscription first
      let subscription = await reg.pushManager.getSubscription();
      
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
        console.log('[PWA] New push subscription created');
      }

      setPushSubscription(subscription);
      
      // Send subscription to server via the shared axios instance.
      try {
        await api.post('/notifications/subscribe', {
          subscription: subscription.toJSON(),
          app_type: appType,
        });
        console.log('[PWA] Push subscription registered with backend');
      } catch (e) {
        console.error('[PWA] Failed to register push subscription:', e);
      }
      return subscription;
    } catch (error) {
      console.error('[PWA] Push subscription error:', error);
      return null;
    }
  }, [swRegistration, appType, api]);

  // Unsubscribe from push
  const unsubscribeFromPush = useCallback(async () => {
    if (pushSubscription) {
      try {
        await pushSubscription.unsubscribe();
        setPushSubscription(null);
        
        // Notify server
        try {
          await api.post('/notifications/unsubscribe', {});
        } catch (e) {
          // Best effort — silent unsubscribe still succeeded client-side.
        }
        
        return true;
      } catch (error) {
        console.error('[PWA] Unsubscribe error:', error);
        return false;
      }
    }
    return false;
  }, [pushSubscription, api]);

  // Show local notification
  const showNotification = useCallback(async (title, options = {}) => {
    if (Notification.permission !== 'granted') {
      console.log('[PWA] Notification permission not granted');
      return false;
    }

    const defaultOptions = {
      icon: `/icons/${appType === 'owner-operator' ? 'owner-op' : appType}-icon.svg`,
      badge: `/icons/${appType === 'owner-operator' ? 'owner-op' : appType}-icon.svg`,
      vibrate: [200, 100, 200],
      ...options
    };

    if (swRegistration) {
      await swRegistration.showNotification(title, defaultOptions);
    } else {
      new Notification(title, defaultOptions);
    }
    
    return true;
  }, [swRegistration, appType]);

  // Check for service worker updates
  const checkForUpdates = useCallback(async () => {
    if (swRegistration) {
      await swRegistration.update();
    }
  }, [swRegistration]);

  return {
    // State
    isInstallable,
    isInstalled,
    isOnline,
    pushSupported,
    pushPermission,
    pushSubscription,
    
    // Actions
    installApp,
    requestPushPermission,
    subscribeToPush,
    unsubscribeFromPush,
    showNotification,
    checkForUpdates
  };
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default usePWA;
